<script setup>
import OrderItem from '@/components/general/OrderItem.vue'
import { useCustomerStore } from '@/stores/customerStore'
import { storeToRefs } from 'pinia'
import { onMounted } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/router/routeNames'
import mixpanel from '@/services/mixpanel'

// Use the customer store
const customerStore = useCustomerStore()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)

// Extract reactive refs from the store
const { recentOrders, isLoading, isError } = storeToRefs(customerStore)

// Fetch data when component mounts
onMounted(() => {
  customerStore.refreshData()
})
</script>

<template>
  <div class="sm:py-12" v-if="!isLoading">
    <div class="sm:px-6 lg:px-8">
      <div class="mb-6 text-xl font-bold text-gray-900 sm:text-2xl dark:text-white">Welcome {{ user?.name }}</div>

      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center items-center py-10">
        <div
          class="w-12 h-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
      </div>

      <!-- Error state -->
      <div
        v-else-if="isError"
        class="relative px-4 py-3 mb-6 text-red-700 bg-red-100 rounded border border-red-400">
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline">There was a problem loading your dashboard data.</span>
        <button
          @click="customerStore.refreshData()"
          class="px-4 py-2 mt-2 text-white bg-red-600 rounded hover:bg-red-700">
          Retry
        </button>
      </div>

      <!-- Content -->
      <div v-else class="grid grid-cols-1 gap-6 md:grid-cols-2">


        <!-- Quick Actions -->
        <div class="overflow-hidden self-start p-6 bg-white shadow-xl dark:bg-gray-800 sm:rounded-lg">
          <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Quick Actions</h3>
          <div class="space-y-4">
            <RouterLink :to="{ name: ROUTES.BUSINESSES }"
              @click="mixpanel.track('Browse Businesses / Place New Order', { distinct_id: user.id })"
              class="flex justify-center items-center p-4 w-full text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600">
              <svg class="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Browse Businesses / Place New Order
            </RouterLink>
            <!-- <Link :href="route('orders.create')"
                                class="flex justify-center items-center p-4 w-full text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600">
                                <svg class="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Place New Order
                            </Link> -->
          </div>
        </div>
        <!-- Recent Orders -->
        <div class="overflow-hidden p-6 bg-white shadow-xl dark:bg-gray-800 sm:rounded-lg">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Orders</h3>
            <RouterLink
              :to="{ name: ROUTES.DASHBOARD_ORDERS }"
              class="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">
              View All
            </RouterLink>
          </div>
          <div v-if="recentOrders.length > 0">
            <OrderItem v-for="order in recentOrders" :key="order.id" :order="order" />
          </div>
          <div v-else class="text-gray-600 dark:text-gray-400">No recent orders</div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="flex justify-center items-center py-10">
    <div
      class="w-12 h-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
  </div>
</template>
